import type { LookupPayload, LookupType } from "./types";

const THAI_DIGITS: Record<string, string> = {
  "๐": "0",
  "๑": "1",
  "๒": "2",
  "๓": "3",
  "๔": "4",
  "๕": "5",
  "๖": "6",
  "๗": "7",
  "๘": "8",
  "๙": "9",
};

const WILDCARD_PATTERN = /[%*_?]/;

export interface NormalizedLookup {
  type: LookupType;
  value?: string;
  fullName?: string;
  dob?: string;
  phone?: string;
}

export interface ValidationResult {
  ok: boolean;
  message?: string;
  lookup?: NormalizedLookup;
}

export function toArabicDigits(value: string): string {
  return value.replace(/[๐-๙]/g, (digit) => THAI_DIGITS[digit] ?? digit);
}

export function normalizeSpaces(value: string): string {
  return toArabicDigits(value).trim().replace(/\s+/g, " ");
}

export function normalizeHN(value: string): string {
  return normalizeSpaces(value).toUpperCase().replace(/[\s\-]/g, "");
}

export function normalizeNumericIdentifier(value: string): string {
  return toArabicDigits(value).replace(/\D/g, "");
}

export function normalizePhone(value: string): string {
  const digits = normalizeNumericIdentifier(value);
  if (digits.startsWith("66") && digits.length >= 11) return `0${digits.slice(2)}`;
  return digits;
}

export function normalizeName(value: string): string {
  return normalizeSpaces(value).toLocaleLowerCase("th-TH");
}

export function hasWildcards(value: string): boolean {
  return WILDCARD_PATTERN.test(value);
}

export function getLookupDisplayName(type: LookupType): string {
  const names: Record<LookupType, string> = {
    hn: "HN",
    national_id: "เลขบัตรประชาชน",
    person_combo: "ชื่อ + วันเกิด/เบอร์โทร",
  };
  return names[type];
}

export function validateLookupPayload(payload: LookupPayload): ValidationResult {
  if (!payload || !payload.type) {
    return { ok: false, message: "กรุณาเลือกวิธีค้นหา" };
  }

  if (payload.type === "person_combo") {
    const fullName = normalizeName(payload.fullName ?? "");
    const dob = normalizeSpaces(payload.dob ?? "");
    const phone = normalizePhone(payload.phone ?? "");

    if (!fullName || !dob || !phone) {
      return { ok: false, message: "กรุณาระบุชื่อ วันเกิด และเบอร์โทรให้ครบ" };
    }
    if (hasWildcards(fullName) || hasWildcards(phone)) {
      return { ok: false, message: "ระบบรับเฉพาะข้อมูลแบบตรงตัวเท่านั้น" };
    }
    if (fullName.length < 4 || !/^\d{4}-\d{2}-\d{2}$/.test(dob) || phone.length < 9) {
      return { ok: false, message: "ข้อมูลค้นหายังไม่ครบพอสำหรับการตรวจสอบสิทธิ" };
    }

    return { ok: true, lookup: { type: payload.type, fullName, dob, phone } };
  }

  const rawValue = normalizeSpaces(payload.value ?? "");
  if (!rawValue || hasWildcards(rawValue)) {
    return { ok: false, message: "ระบบรับเฉพาะข้อมูลแบบตรงตัวเท่านั้น" };
  }

  if (payload.type === "hn") {
    const value = normalizeHN(rawValue);
    if (!/^\d{3,}$/.test(value)) return { ok: false, message: "กรุณาระบุ HN เป็นตัวเลขอย่างน้อย 3 หลัก" };
    return { ok: true, lookup: { type: payload.type, value } };
  }

  if (payload.type === "national_id") {
    const value = normalizeNumericIdentifier(rawValue);
    if (value.length !== 13) return { ok: false, message: "กรุณาระบุเลขบัตรประชาชน 13 หลัก" };
    return { ok: true, lookup: { type: payload.type, value } };
  }

  return { ok: false, message: "ไม่รองรับวิธีค้นหานี้" };
}
